import * as Sentry from '@sentry/react'

/**
 * Product-analytics telemetry, built on Sentry Application Metrics (`Sentry.metrics`).
 *
 * This is the ONLY module that touches the Sentry metrics SDK — every counter in the app
 * funnels through {@link trackMetric}. If Sentry renames/moves the metrics API, this is the
 * single file to change.
 *
 * Design rules:
 *  - Few metric names, many attributes. Don't add a new metric name per button; add an
 *    `action`/`stage`/`chart_type` attribute value instead. High-cardinality attributes are
 *    exactly what Application Metrics is built for (group-by / filter in the Metrics Explorer).
 *  - Every metric automatically carries the org bundle (`org_id`, `org_type`) so analytics can
 *    always segment by organisation without each call site remembering to attach it.
 *  - Telemetry must NEVER break a user action: all emission is wrapped so a failure is swallowed.
 */

/** The full set of counter names we emit. Keep this small — prefer attributes over new names. */
export type MetricName =
  | 'order_view.action'
  | 'order.lifecycle'
  | 'order.edited'
  | 'order.page_view'
  | 'order.dwell_time'
  | 'order.list_view'
  | 'order.section'
  | 'order.tab_switch'
  | 'order.submit_blocked'
  | 'viz.opened'
  | 'viz.template_selected'
  | 'project.action'
  | 'report.download'
  | 'app.boot'
  | 'viz.chart'
  | 'viz.menu'
  | 'viz.source_toggle'
  | 'viz.mode'
  | 'viz.krona_mount'
  | 'auth.action'
  | 'billing.action'
  | 'org.action'
  | 'order_creation.option_selected'
  | 'upload.file_size'
  | 'upload.file_count'
  | 'upload.outcome'
  | 'order_wizard.primer_estimate'
  | 'order_wizard.funnel'
  | 'order_wizard.analysis'

export type MetricAttributes = Record<string, string | number | boolean>

/**
 * The current user's organisation, kept in module scope so any call site (component, epic,
 * reducer) can emit a fully-attributed metric without threading org through props.
 * Set from AuthenticationSlice on login/refresh and cleared on logout.
 */
let currentOrg: { id: string; type: string; isInternal: boolean } | null = null
let currentUserId: string | null = null

export const setTelemetryOrg = (org: { id: string; type: string; isInternal?: boolean } | null, userId?: string | null): void => {
  currentOrg = org ? { id: org.id, type: org.type, isInternal: org.isInternal ?? false } : null
  currentUserId = userId ?? null
}

export const getTelemetryOrg = (): { id: string; type: string; isInternal: boolean } | null => currentOrg

/**
 * Attributes attached to every metric. `is_internal` is driven by the Keycloak org attribute
 * `org_internal` — filter dashboards with `!is_internal:true` to drop test/power-user noise without
 * hardcoding any org IDs. `user_id` enables per-user / distinct-user analysis (group by it); absent
 * on pre-login events (registration/login) since the user isn't resolved yet.
 */
const baseAttributes = (): MetricAttributes => {
  const attrs: MetricAttributes = {}
  if (currentOrg) {
    attrs.org_id = currentOrg.id
    attrs.org_type = currentOrg.type
    attrs.is_internal = currentOrg.isInternal
  }
  if (currentUserId) attrs.user_id = currentUserId
  return attrs
}

/** Drop undefined/null values so we never send empty attributes. */
const clean = (attrs: Record<string, string | number | boolean | undefined | null>): MetricAttributes => {
  const out: MetricAttributes = {}
  for (const [key, value] of Object.entries(attrs)) {
    if (value !== undefined && value !== null) out[key] = value
  }
  return out
}

/**
 * Increment a counter by 1, with the org bundle merged in automatically.
 * Prefer the typed domain helpers (trackOrderAction, trackVizOpened, …) over calling this directly.
 */
export const trackMetric = (name: MetricName, attributes: Record<string, string | number | boolean | undefined | null> = {}): void => {
  try {
    Sentry.metrics.count(name, 1, { attributes: { ...baseAttributes(), ...clean(attributes) } })
  } catch {
    /* telemetry must never throw into a user action */
  }
}

/**
 * Record a numeric value into a distribution, for stats aggregates (p50/p75/p90/p95/p99, avg, …).
 * Use for magnitudes that vary — file sizes, durations, amounts. `unit` (e.g. 'byte', 'millisecond',
 * 'usd') is rendered on Sentry's axes. Org bundle + is_internal are attached automatically.
 */
export const trackDistribution = (
  name: MetricName,
  value: number,
  attributes: Record<string, string | number | boolean | undefined | null> = {},
  unit?: string
): void => {
  try {
    Sentry.metrics.distribution(name, value, { unit, attributes: { ...baseAttributes(), ...clean(attributes) } })
  } catch {
    /* telemetry must never throw into a user action */
  }
}

/**
 * Set a gauge (latest-value-wins point-in-time level). Rarely the right tool on the frontend —
 * provided for completeness; prefer counters for frequency and distributions for magnitudes.
 */
export const trackGauge = (
  name: MetricName,
  value: number,
  attributes: Record<string, string | number | boolean | undefined | null> = {},
  unit?: string
): void => {
  try {
    Sentry.metrics.gauge(name, value, { unit, attributes: { ...baseAttributes(), ...clean(attributes) } })
  } catch {
    /* telemetry must never throw into a user action */
  }
}

/**
 * Emitted once per SPA boot. `nav_type` distinguishes a hard reload from in-app navigation, so the
 * reload rate (and spikes after a release) are visible. Measured on the fresh load — reliable,
 * unlike trying to report from a dying/hung tab. Fires before auth, so it usually has no org bundle.
 */
export const trackAppBoot = (): void => {
  const navType = (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined)?.type
  trackMetric('app.boot', { nav_type: navType ?? 'unknown' })
}
